// /firmware — 韌體釋出管理(V4P-1,自 ls-v4 admin 移植的產品設計;底座=
// firmware-server v0.2 引擎,經 restful /v3/admin/fw/* 白名單代理)。
// 三段:model chips(自 images 萃取)→ releases 表(生命週期動作:
// activate/pause/resume/rollback,一律經 ConfirmDialog 重述後果確認)→ images/targeting 唯讀。
// kill switch=pause(00 §3.2;v4 stopFOTA 對應物)。動作 server 端寫 audit。
// pause(kill switch)/rollback(不可逆)在 ConfirmDialog 內必填原因+案件編號
// 送入 audit_logs(#73;缺任一後端 400,前端 gate 確認鈕);activate/resume 選填。
//
// 視覺改版(新設計系統):
// - Release 生命週期 / 韌體映像檔 / Beta 名單 三段從「直向全展開堆疊」
//   改成「主操作卡 + adm-tabs 內容分頁」,操作區與唯讀參考區分權重。
// - 危險動作(暫停=kill switch、回滾)改用 btn-danger,與 activate/resume
//   的 btn-ghost 視覺區隔,操作者按下前就能分辨風險。
// - 狀態 pill 依 STATE_PILL_CLASS 對照表上色,不必逐字讀就能分辨現況。
// - 全中文化:section 標題、表頭皆改中文;技術性欄名(release_id/
//   owner_scope)保留英數但降階為 mono 附註,不當主標題用。
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { canAdminWrite, useAuth } from '../../auth/AuthContext';
import { EmptyState, ErrorState, LoadingState } from '../../components/StateBlock';
import { ConfirmDialog } from '../_shared/ConfirmDialog';
import { ApiError } from '../../lib/api';
import { formatTimestampUs } from '../../lib/format';
import {
  FWAction, FWImage, FWRelease, FWTargeting,
  listImages, listKnownModels, listReleases, listTargeting, releaseAction,
} from './fwApi';
import '../_shared/shared.css';
import './firmware.css';

const STATE_LABEL: Record<string, string> = {
  draft: '草稿', active: '現役', paused: '已暫停', superseded: '已被取代', rolled_back: '已回滾',
};

// 釋出通路中文化(firmware-server domain ChannelStable/Beta + canary 先行);
// 純顯示,未知值退原值 + title(不影響送 API/比較)。
const CHANNEL_LABEL: Record<string, string> = {
  stable: '穩定版', beta: 'Beta 測試版', canary: 'Canary 先行版',
};

// 投放規則類型中文化(firmware-server 00 §3.3 TargetPin/Exclude/Force/Beta);
// 純顯示,未知值退原值 + title。
const KIND_LABEL: Record<string, string> = {
  pin: '版本鎖定', exclude: '排除更新', force: '強制更新', beta: 'Beta 名單',
};

// 審核狀態中文化(純顯示;未知值原樣印出,不影響送 API/比較)。
const REVIEW_STATE_LABEL: Record<string, string> = {
  pending: '待審核', in_review: '審核中', approved: '已核准',
  rejected: '已駁回', changes_requested: '需修改', none: '無需審核',
};

// ADR-050 staged-stable rollout 階段中文化(firmware-server domain rollout
// FWStage*);純顯示,未知值退原值 + title(不影響送 API/比較)。
const STAGE_LABEL: Record<string, string> = {
  lab: '實驗室', internal: '內部', canary: '金絲雀', early: '早期', broad: '廣泛', complete: '全量',
};

// 健康評估狀態中文化(firmware-server domain rollout OTAHealth*)。
const HEALTH_LABEL: Record<string, string> = {
  healthy: '健康', unhealthy: '不健康', insufficient_data: '資料不足',
};

// 健康評估 pill 色階(健康=good、不健康=danger、資料不足=warn)。
const HEALTH_PILL_CLASS: Record<string, string> = {
  healthy: 'pill-good', unhealthy: 'pill-danger', insufficient_data: 'pill-warn',
};

// 狀態 pill 色階對照(生命週期狀態一眼分色,不必讀文字)。
const STATE_PILL_CLASS: Record<string, string> = {
  active: 'pill-good',
  paused: 'pill-warn',
  rolled_back: 'pill-danger',
  superseded: 'pill-neutral',
  draft: 'pill-neutral',
};

// 審核狀態 pill 色階(比照 STATE_PILL_CLASS;待審/需修改=warn、審核中=info、
// 已核准=good、已駁回=danger、無需審核=neutral),與「狀態」欄一致 pill 化。
const REVIEW_STATE_PILL_CLASS: Record<string, string> = {
  pending: 'pill-warn',
  in_review: 'pill-info',
  approved: 'pill-good',
  rejected: 'pill-danger',
  changes_requested: 'pill-warn',
  none: 'pill-neutral',
};

// 動作可用性(依 firmware-server 00 §3.2 狀態機 + ADR-050 staged rollout;
// 伺服端仍是權威,按錯/CAS 衝突/健康未過頂多 409 CONFLICT)。active 且為
// staged-stable(有 stage 且未到 complete)才提供「推進」——鏡射 portal
// allowedActions;推進是否真的可執行由後端健康守門 + eligible 時間 + CAS 判定。
function actionsFor(r: FWRelease): FWAction[] {
  switch (r.state) {
    case 'active':
      return r.channel === 'stable' && !!r.stage && r.stage !== 'complete'
        ? ['advance', 'pause', 'rollback']
        : ['pause', 'rollback'];
    case 'paused': return ['resume'];
    case 'draft':
    case 'superseded':
    case 'rolled_back': return ['activate'];
    default: return [];
  }
}

const ACTION_LABEL: Record<FWAction, string> = {
  activate: '啟用', pause: '暫停', resume: '恢復', rollback: '回滾', advance: '推進',
};

// 映像檔「範圍」(owner_scope)標籤化:平台級 → 「平台」,組織級 org#<id> → 顯示組織代號,
// 其餘原樣;避免 raw scope 字串直接外洩到表格。
function ownerScopeLabel(scope?: string): string {
  if (!scope) return '—';
  if (scope === 'platform') return '平台';
  if (scope.startsWith('org#')) return scope.slice(4);
  return scope;
}

// 高風險動作:暫停(kill switch)與回滾——按鈕用 danger 描邊示警,
// 點擊後開 ConfirmDialog(tone=danger)重述後果再執行;activate/resume 維持中性 ghost。
const DANGER_ACTIONS: FWAction[] = ['pause', 'rollback'];

// ConfirmDialog body 的後果重述:讓操作者按下確認前就看到這一刀的實際影響。
const ACTION_IMPACT: Record<FWAction, string> = {
  activate: '將啟用此版本,開始對符合條件的裝置下發 OTA。',
  pause: '將立即停止此版本的 OTA 下發(緊急中止)。',
  resume: '將恢復此版本的 OTA 下發。',
  rollback: '將回滾此版本,裝置將停止取得此版本。',
  advance: '將把此版本推進到下一個釋出階段,擴大 OTA 下發範圍;若健康守門未通過、尚未到可推進時間,或版次已被其他操作變更,後端會拒絕(409)。',
};

type FwTab = 'releases' | 'images' | 'targeting';

export default function FirmwarePage() {
  const { role } = useAuth();
  const writable = canAdminWrite(role); // cs-readonly 直連本頁時:生命週期動作收起,清單仍可檢視
  const [knownModels, setKnownModels] = useState<string[] | null>(null);
  const [images, setImages] = useState<FWImage[]>([]);
  const [model, setModel] = useState<string>('');
  const [modelInput, setModelInput] = useState<string>('');
  const [releases, setReleases] = useState<FWRelease[] | null>(null);
  const [targeting, setTargeting] = useState<FWTargeting[]>([]);
  const [targetingUnavailable, setTargetingUnavailable] = useState(false); // by-model 查詢失敗/未支援 → 不謊報「無規則」
  const [err, setErr] = useState<string | null>(null);
  // 初始機型清單載入錯誤(原始物件,供 ErrorState 判 403 + 取 request_id);
  // 與行內動作錯誤 err 分離,避免載入失敗落到「請先選擇機型」的假空狀態。
  const [loadErr, setLoadErr] = useState<unknown>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [pending, setPending] = useState<{ rel: FWRelease; action: FWAction } | null>(null); // 待 ConfirmDialog 確認的動作
  const [reason, setReason] = useState(''); // 操作理由(pause/rollback 必填,#73)
  const [caseId, setCaseId] = useState(''); // 案件編號(pause/rollback 必填,#73)
  const [notice, setNotice] = useState<string | null>(null);
  const [tab, setTab] = useState<FwTab>('releases');
  const noticeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const models = useMemo(() => knownModels ?? [], [knownModels]);

  // notice 3.5 秒後自動消失,成功訊息不再卡在頁首(比照 devreview)。
  useEffect(() => {
    if (!notice) return;
    if (noticeTimer.current) clearTimeout(noticeTimer.current);
    noticeTimer.current = setTimeout(() => setNotice(null), 3500);
    return () => { if (noticeTimer.current) clearTimeout(noticeTimer.current); };
  }, [notice]);

  // 機型清單載入:失敗改由 ErrorState 呈現(含 403 無權限 + request_id + 重試)。
  const loadModels = useCallback(() => {
    setLoadErr(null);
    setKnownModels(null);
    listKnownModels()
      .then(setKnownModels)
      .catch((e) => setLoadErr(e));
  }, []);

  useEffect(() => {
    loadModels();
  }, [loadModels]);

  const loadModel = useCallback(async (m: string) => {
    setModel(m);
    setReleases(null);
    setErr(null);
    setPending(null);
    setTargetingUnavailable(false); // 換機型先清,避免沿用上一台的查詢失敗旗標
    setTab('releases');
    try {
      const [rel, imgs] = await Promise.all([listReleases(m), listImages(m)]);
      rel.sort((a, b) => (b.created_at ?? 0) - (a.created_at ?? 0));
      setReleases(rel);
      setImages(imgs);
    } catch (e) {
      setErr(e instanceof Error ? e.message : '載入失敗');
      return;
    }
    // by-model 投放規則查詢已上線(firmware-server#5:?model= = model-index Scan)——
    // 仍容錯:真的查詢失敗時留空並標記,空狀態才不會把「查不到」謊報成「無投放規則」。
    try {
      setTargeting(await listTargeting(m));
      setTargetingUnavailable(false);
    } catch {
      setTargeting([]);
      setTargetingUnavailable(true);
    }
  }, []);

  // 開 ConfirmDialog:先清空上一次的理由/案件欄,避免沿用殘值。
  const openConfirm = (rel: FWRelease, action: FWAction) => {
    setReason('');
    setCaseId('');
    setErr(null);
    setPending({ rel, action });
  };

  // ConfirmDialog 按下確認後才實際打 API;pause/rollback 帶 reason/case_id
  // (後端必填、寫入 audit_logs,#73);錯誤回頁首 result 條。
  const runAction = async (rel: FWRelease, action: FWAction) => {
    const key = `${rel.release_id}:${action}`;
    setBusy(key);
    setErr(null);
    try {
      await releaseAction(model, rel.release_id, action, { reason: reason.trim(), case_id: caseId.trim() });
      setNotice(`${ACTION_LABEL[action]}完成:${rel.version}(${rel.release_id})`);
      await loadModel(model);
    } catch (e) {
      // 409 CONFLICT:CAS 版次落後或推進守門未過(健康未通過/未到可推進時間,
      // ROLLOUT_STAGE_GATE)。給明確提示並要求重新整理取回最新版次,而非只印原始訊息。
      if (e instanceof ApiError && e.status === 409) {
        setErr(
          `${ACTION_LABEL[action]}未執行:此版本狀態已變更或尚未符合推進條件` +
          `(版次衝突/健康守門未通過)。請「重新整理」取得最新狀態後再試。` +
          (e.message ? `(${e.message})` : '')
        );
      } else {
        setErr(e instanceof Error ? e.message : `${ACTION_LABEL[action]}失敗`);
      }
    } finally {
      setBusy(null);
      setPending(null);
    }
  };

  const modelImages = images;

  // pause(kill switch)/rollback(不可逆)必填原因+案件編號(#73);未帶則 gate
  // 住確認鈕(後端亦擋:缺任一→400);activate/resume 選填。
  const confirmReasonRequired = pending ? DANGER_ACTIONS.includes(pending.action) : false;
  const confirmJustificationReady = reason.trim() !== '' && caseId.trim() !== '';

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">韌體釋出</h1>
          <p className="page-subtitle">管理各機型的韌體釋出:上線、暫停(緊急停止)、回滾與 Beta 投放;所有動作都會寫入稽核紀錄。</p>
        </div>
        {model && <button className="btn-ghost" onClick={() => void loadModel(model)}>重新整理</button>}
      </div>

      {notice && <div className="adm-action-result is-ok" role="status" data-testid="fw-notice">{notice}</div>}
      {err && <div className="adm-action-result is-err" role="alert">{err}</div>}

      {loadErr ? (
        // 載入失敗:改渲染 ErrorState(403→無權限 + request_id + 重試),不落到假空狀態
        <ErrorState error={loadErr} onRetry={loadModels} />
      ) : knownModels === null ? (
        <LoadingState />
      ) : (
        <div className="toolbar-panel fw-model-toolbar">
          <p className="fw-model-source-hint text-tertiary">
            列出所有已有韌體釋出或映像檔的機型;若要查看尚無韌體的機型,請用右側自訂型號。
          </p>
          <div className="adm-chips fw-model-chips" data-testid="fw-models">
            {models.map((m) => (
              <button
                key={m}
                className={m === model ? 'btn-primary' : 'btn-ghost'}
                onClick={() => void loadModel(m)}
                data-testid={`fw-model-${m}`}
              >
                {m}
              </button>
            ))}
          </div>
          <div className="fw-model-custom">
            <span className="fw-model-custom-label">或輸入自訂型號</span>
            <input
              placeholder="機型名稱…"
              value={modelInput}
              onChange={(e) => setModelInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && modelInput.trim()) void loadModel(modelInput.trim()); }}
              data-testid="fw-model-input"
            />
            <button
              className="btn-ghost btn-sm"
              disabled={!modelInput.trim()}
              onClick={() => { if (modelInput.trim()) void loadModel(modelInput.trim()); }}
              data-testid="fw-model-go"
            >
              查看
            </button>
          </div>
        </div>
      )}

      {!loadErr && !model && (
        <div className="empty-state">
          <div className="empty-state-icon">📦</div>
          <div className="empty-state-title">請先選擇機型</div>
          <div className="empty-state-detail">選擇上方機型(或輸入自訂型號)以檢視該機型的版本生命週期、韌體映像檔與 Beta 名單。</div>
        </div>
      )}

      {model && (
        <section className="card card--primary">
          <div className="section-head">
            <h2 className="section-title">{model}</h2>
            <div className="adm-tabs">
              <button
                type="button"
                className={`adm-tab${tab === 'releases' ? ' is-active' : ''}`}
                onClick={() => setTab('releases')}
                data-testid="fw-tab-releases"
              >
                釋出生命週期
              </button>
              <button
                type="button"
                className={`adm-tab${tab === 'images' ? ' is-active' : ''}`}
                onClick={() => setTab('images')}
                data-testid="fw-tab-images"
              >
                韌體映像檔
              </button>
              <button
                type="button"
                className={`adm-tab${tab === 'targeting' ? ' is-active' : ''}`}
                onClick={() => setTab('targeting')}
                data-testid="fw-tab-targeting"
              >
                Beta 名單
              </button>
            </div>
          </div>

          {tab === 'releases' && (
            <div>
              {releases === null ? (
                <LoadingState />
              ) : releases.length === 0 ? (
                <EmptyState title="此機型尚無釋出" />
              ) : (
                <div className="table-scroll">
                  <table className="data-table" data-testid="fw-releases">
                    <thead>
                      <tr>
                        <th>版本</th><th>硬體版本</th><th>通路</th><th>狀態</th><th>審核</th><th>釋出階段</th><th>健康</th><th>成功/失敗</th><th>建立時間</th><th>動作</th>
                      </tr>
                    </thead>
                    <tbody>
                      {releases.map((r) => {
                        const failed = r.stats?.failed ?? 0;
                        const succeeded = r.stats?.succeeded ?? 0;
                        return (
                          <tr key={r.release_id} data-testid={`fw-rel-${r.version}`}>
                            <td>
                              <div className="adm-cell-strong">{r.version}</div>
                              <div className="adm-mono text-tertiary fw-rel-id">{r.release_id}</div>
                            </td>
                            <td>{r.hw_version}</td>
                            <td><span title={r.channel}>{CHANNEL_LABEL[r.channel] ?? r.channel}</span></td>
                            <td>
                              <span className={`pill ${STATE_PILL_CLASS[r.state] ?? 'pill-neutral'}`} data-testid={`fw-state-${r.version}`}>
                                {STATE_LABEL[r.state] ?? r.state}
                              </span>
                              {r.state === 'paused' && r.pause_reason && (
                                <div className="text-tertiary fw-pause-reason" title={r.pause_reason} data-testid={`fw-pause-reason-${r.version}`}>
                                  自動暫停:{r.pause_reason}
                                </div>
                              )}
                            </td>
                            <td>
                              {r.review_state ? (
                                <span className={`pill ${REVIEW_STATE_PILL_CLASS[r.review_state] ?? 'pill-neutral'}`}>
                                  {REVIEW_STATE_LABEL[r.review_state] ?? r.review_state}
                                </span>
                              ) : (
                                '—'
                              )}
                            </td>
                            <td data-testid={`fw-stage-cell-${r.version}`}>
                              {r.stage ? (
                                <div className="adm-cell-strong" title={r.stage}>{STAGE_LABEL[r.stage] ?? r.stage}</div>
                              ) : (
                                <span className="text-tertiary">—</span>
                              )}
                              {r.stage_eligible_at ? (
                                <div className="text-tertiary fw-stage-eligible">可推進 {formatTimestampUs(r.stage_eligible_at)}</div>
                              ) : null}
                              {typeof r.state_revision === 'number' ? (
                                <div className="adm-mono text-tertiary fw-state-rev" title="版次(推進/動作的樂觀鎖)">rev {r.state_revision}</div>
                              ) : null}
                            </td>
                            <td data-testid={`fw-health-cell-${r.version}`}>
                              {r.last_health?.evaluation_status ? (
                                <span className={`pill ${HEALTH_PILL_CLASS[r.last_health.evaluation_status] ?? 'pill-neutral'}`} title={r.last_health.evaluation_status}>
                                  {HEALTH_LABEL[r.last_health.evaluation_status] ?? r.last_health.evaluation_status}
                                </span>
                              ) : r.health_passed ? (
                                <span className="pill pill-good">通過</span>
                              ) : (
                                <span className="text-tertiary">—</span>
                              )}
                              {r.lab_evidence_ready ? (
                                <div className="text-tertiary fw-lab-evidence">實測證據就緒</div>
                              ) : null}
                            </td>
                            <td>
                              <span className={succeeded > 0 ? 'fw-success-count' : 'text-tertiary'}>{succeeded}</span>
                              {' / '}
                              <span className={failed > 0 ? 'fw-fail-count' : 'text-tertiary'}>{failed}</span>
                            </td>
                            <td>{r.created_at ? formatTimestampUs(r.created_at) : '—'}</td>
                            <td>
                              {writable ? (
                                <div className="fw-action-cell">
                                  {actionsFor(r).map((a) => {
                                    const isDanger = DANGER_ACTIONS.includes(a);
                                    return (
                                      <button
                                        key={a}
                                        className={`${isDanger ? 'btn-danger' : 'btn-ghost'} btn-sm`}
                                        disabled={busy !== null}
                                        onClick={() => openConfirm(r, a)}
                                        data-testid={`fw-act-${r.version}-${a}`}
                                      >
                                        {ACTION_LABEL[a]}
                                      </button>
                                    );
                                  })}
                                </div>
                              ) : (
                                <span className="text-tertiary">唯讀</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {tab === 'images' && (
            <div className="fw-readonly-panel">
              {modelImages.length === 0 ? (
                <EmptyState title="無韌體映像檔" />
              ) : (
                <div className="table-scroll">
                  <table className="data-table" data-testid="fw-images">
                    <thead><tr><th>版本</th><th>硬體版本</th><th>範圍</th><th>簽章</th><th>上傳時間</th></tr></thead>
                    <tbody>
                      {modelImages.map((i) => (
                        <tr key={`${i.version}-${i.hw_version ?? ''}`}>
                          <td>{i.version}</td>
                          <td>{i.hw_version ?? '*'}</td>
                          <td><span className="pill pill-neutral" title={i.owner_scope}>{ownerScopeLabel(i.owner_scope)}</span></td>
                          <td>
                            {i.signature ? (
                              <code className="adm-mono" title={i.signature}>{i.signature.slice(0, 16)}…</code>
                            ) : (
                              <span className="pill pill-warn">無簽章</span>
                            )}
                          </td>
                          <td>{i.created_at ? formatTimestampUs(i.created_at) : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {tab === 'targeting' && (
            <div className="fw-readonly-panel">
              {targeting.length === 0 ? (
                // 內部備註:投放規則由 /v3/dev 寫入(common/08 §8.2);by-model(?model=)
                // 查詢已上線(firmware-server#5),查詢失敗時才留空並標記,不謊報「無規則」。
                targetingUnavailable ? (
                  <EmptyState
                    title="暫時無法查詢投放規則"
                    detail="查詢此機型的投放規則時發生錯誤,無法確認是否有規則;請稍後重新整理,或改由開發者平台檢視。"
                  />
                ) : (
                  <EmptyState title="此機型目前無投放規則" detail="Beta 名單、強制更新與版本鎖定的投放規則會列在這裡。" />
                )
              ) : (
                <div className="table-scroll">
                  <table className="data-table" data-testid="fw-targeting">
                    <thead><tr><th>範圍</th><th>類型</th><th>裝置數</th></tr></thead>
                    <tbody>
                      {targeting.map((t, i) => (
                        <tr key={i}>
                          <td><code className="adm-mono">{t.scope}</code></td>
                          <td><span title={t.kind}>{KIND_LABEL[t.kind] ?? t.kind}</span></td>
                          <td>{t.devices?.length ?? 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </section>
      )}

      {pending && (
        <ConfirmDialog
          title={`${ACTION_LABEL[pending.action]} release`}
          tone={DANGER_ACTIONS.includes(pending.action) ? 'danger' : 'primary'}
          confirmLabel={ACTION_LABEL[pending.action]}
          busy={busy === `${pending.rel.release_id}:${pending.action}`}
          confirmDisabled={confirmReasonRequired && !confirmJustificationReady}
          body={
            <>
              版本 <strong>{pending.rel.version}</strong>
              (<code className="adm-mono">{pending.rel.release_id}</code>)
              <br />
              {ACTION_IMPACT[pending.action]}
              <div className="fw-confirm-fields">
                <label className="adm-field">
                  <span className="adm-field-label text-secondary">原因{confirmReasonRequired ? ' *' : '（選填）'}</span>
                  <input
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    disabled={busy !== null}
                    placeholder={confirmReasonRequired ? '請說明本次操作原因' : ''}
                    data-testid="fw-confirm-reason"
                  />
                </label>
                <label className="adm-field">
                  <span className="adm-field-label text-secondary">案件編號{confirmReasonRequired ? ' *' : '（選填）'}</span>
                  <input
                    value={caseId}
                    onChange={(e) => setCaseId(e.target.value)}
                    disabled={busy !== null}
                    placeholder={confirmReasonRequired ? '如 HD-1234' : ''}
                    data-testid="fw-confirm-case"
                  />
                </label>
              </div>
              {confirmReasonRequired && (
                <p className="text-tertiary fw-confirm-hint">此動作不可逆,原因與案件編號為必填,將一併寫入稽核紀錄。</p>
              )}
            </>
          }
          onConfirm={() => void runAction(pending.rel, pending.action)}
          onCancel={() => setPending(null)}
        />
      )}
    </div>
  );
}
